import type * as React from 'react'
import { useMemo } from 'react'
import {
  faChartTreeMap,
  faEnvelope,
  faReceipt,
  faWarehouse,
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import SimpleBar from 'simplebar-react'

import { Logo } from 'Components/Logo/Logo'
import { PortalMenu } from 'Components/PortalMenu/PortalMenu'

import { useStore } from 'Stores/index'
import { UserModeEnum } from 'Stores/portalSettingsStore/portalSettingsStore.types'

import {
  useActiveUser,
  useIsOrganisation,
  useShowGlobalSearch,
  useTheme,
} from 'Hooks'

import { PORTAL_CONTENT_MODEL } from 'Constants/portalContentModel'
import {
  PORTAL_HEADING_TRANSLATION_IDS,
  PortalSectionEnum,
} from 'Constants/portalSection'

import { ContentTypeEnum } from 'Types/content/content.types'

import poweredByLogo from 'Assets/images/logos/powered_by_everphone.svg'

const simpleBarStyles: React.CSSProperties = {
  maxHeight: '100%',
  overflowX: 'hidden',
}

export const LeftSidebar = observer(() => {
  const { portalSettingsStore, navigationStore, portalConfigStore } = useStore()
  const { isCompactSidebar, isMenuVisible, toggleMenuVisibility, userMode } =
    portalSettingsStore
  const { mainNavigationConfig } = navigationStore
  const { portalConfig } = portalConfigStore

  const { activeEmployee, activeOrganisation } = useActiveUser()
  const shouldShowGlobalSearch = useShowGlobalSearch()
  const isOrganisation = useIsOrganisation()

  const { isEverphoneTheme } = useTheme()

  const menuItems = useMemo(
    () => [
      {
        icon: faChartTreeMap,
        id: 0,
        key: PortalSectionEnum.Dashboard,
        name: PORTAL_HEADING_TRANSLATION_IDS.dashboard,
        path: `${mainNavigationConfig.url}`,
      },
      {
        icon: PORTAL_CONTENT_MODEL[ContentTypeEnum.rentals].icon,
        id: 2,
        key: PortalSectionEnum.Rentals,
        name: PORTAL_HEADING_TRANSLATION_IDS.rentals,
        path: shouldShowGlobalSearch
          ? `${mainNavigationConfig.url}/rentals?searchModel=${
              PORTAL_CONTENT_MODEL[ContentTypeEnum.rentals].modelType
            }`
          : `${mainNavigationConfig.url}/rentals`,
      },
      ...(isOrganisation && activeOrganisation?.phonePlans?.totalCount !== 0
        ? [
            {
              icon: PORTAL_CONTENT_MODEL[ContentTypeEnum.phone_plans].icon,
              id: 3,
              key: PortalSectionEnum.PhonePlans,
              name: PORTAL_HEADING_TRANSLATION_IDS['phone-plans'],
              path: shouldShowGlobalSearch
                ? `${mainNavigationConfig.url}/phone-plans?searchModel=${
                    PORTAL_CONTENT_MODEL[ContentTypeEnum.phone_plans].modelType
                  }`
                : `${mainNavigationConfig.url}/phone-plans`,
            },
          ]
        : []),
      ...(isOrganisation
        ? [
            {
              icon: PORTAL_CONTENT_MODEL[ContentTypeEnum.employees].icon,
              id: 5,
              key: PortalSectionEnum.Employees,
              name: PORTAL_HEADING_TRANSLATION_IDS.employees,
              path: shouldShowGlobalSearch
                ? `${mainNavigationConfig.url}/employees?searchModel=${
                    PORTAL_CONTENT_MODEL[ContentTypeEnum.employees].modelType
                  }`
                : `${mainNavigationConfig.url}/employees`,
            },
          ]
        : []),
      ...(isOrganisation && activeOrganisation?.depots?.totalCount !== 0
        ? [
            {
              icon: faWarehouse,
              id: 6,
              key: PortalSectionEnum.Depots,
              name: PORTAL_HEADING_TRANSLATION_IDS.depots,
              path: `${mainNavigationConfig.url}/depots`,
            },
          ]
        : []),
      ...(isOrganisation &&
      portalConfig?.showInvoices &&
      activeOrganisation?.invoices?.totalCount !== 0
        ? [
            {
              icon: faReceipt,
              id: 7,
              key: PortalSectionEnum.Invoices,
              name: PORTAL_HEADING_TRANSLATION_IDS.invoices,
              path: `${mainNavigationConfig.url}/invoices`,
            },
          ]
        : []),
      {
        icon: PORTAL_CONTENT_MODEL[ContentTypeEnum.caseitems].icon,
        id: 4,
        key: PortalSectionEnum.Orders,
        name: PORTAL_HEADING_TRANSLATION_IDS.orders,
        path: shouldShowGlobalSearch
          ? `${mainNavigationConfig.url}/orders?searchModel=${
              PORTAL_CONTENT_MODEL[ContentTypeEnum.caseitems].modelType
            }`
          : `${mainNavigationConfig.url}/orders`,
      },
      {
        icon: faEnvelope,
        id: 8,
        key: PortalSectionEnum.Contact,
        name: PORTAL_HEADING_TRANSLATION_IDS.contact,
        path: `${mainNavigationConfig.url}/contact`,
      },
    ],
    [
      activeOrganisation,
      isOrganisation,
      mainNavigationConfig,
      portalConfig,
      shouldShowGlobalSearch,
    ],
  )

  return (
    <>
      <div
        className={clsx(
          'left-side-menu',
          'mt-2',
          isMenuVisible && 'left-side-menu-open',
          isCompactSidebar && 'left-side-menu-compact',
          userMode === UserModeEnum.NEW_JOINER && 'left-side-menu-closed',
        )}
      >
        <Logo asLink={userMode !== UserModeEnum.NEW_JOINER} showAdminBadge />

        {!activeOrganisation && !activeEmployee ? null : (
          <SimpleBar
            scrollbarMaxSize={320}
            style={simpleBarStyles}
            timeout={500}
          >
            <PortalMenu menuItems={menuItems} />
          </SimpleBar>
        )}

        {!isEverphoneTheme && !isCompactSidebar && (
          <img
            alt="Powered by Everphone"
            className="powered-by w-100 position-absolute"
            src={poweredByLogo}
          />
        )}
      </div>

      <div
        className={clsx(!isMenuVisible && 'd-none')}
        onClick={toggleMenuVisibility}
        role="none"
      />
    </>
  )
})
